import { memo, type ReactNode } from 'react'

import { TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

import type { DataTableColumn, SortOrder } from './data-table.interface'
import { DataTableCheckbox } from './data-table-checkbox.atom'
import { DataTableColumnHeader } from './data-table-column-header.atom'

interface DataTableHeaderProps<TData> {
  columnList: DataTableColumn<TData>[]
  isSelectable: boolean
  isAllSelected: boolean
  isIndeterminate: boolean
  onSelectAll: (isChecked: boolean) => void
  hasActions: boolean
  onSortChange?: (sortBy: string, sortOrder: SortOrder) => void
  getColumnSortOrder: (columnId: string) => SortOrder | null
  onSort: (columnId: string) => void
}

/**
 * Renders the data table header with column headers, optional selection checkbox, and sorting.
 */
const DataTableHeaderInner = <TData,>({
  columnList,
  isSelectable,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  hasActions,
  onSortChange,
  getColumnSortOrder,
  onSort,
}: DataTableHeaderProps<TData>): ReactNode => (
  <TableHeader>
    <TableRow>
      {isSelectable && (
        <TableHead className="w-[40px]">
          <DataTableCheckbox
            isChecked={isIndeterminate ? 'indeterminate' : isAllSelected}
            onCheckedChange={onSelectAll}
            ariaLabel="Select all rows"
          />
        </TableHead>
      )}
      {columnList.map((column) => (
        <TableHead
          key={column.id}
          style={{ width: column.width }}
          className={cn(column.align === 'right' && 'text-right')}
        >
          <DataTableColumnHeader
            title={column.header}
            isSortable={column.isSortable && !!onSortChange}
            sortOrder={getColumnSortOrder(column.id)}
            onSort={() => onSort(column.id)}
            align={column.align}
          />
        </TableHead>
      ))}
      {hasActions && <TableHead className="w-[60px]" />}
    </TableRow>
  </TableHeader>
)

export const DataTableHeader = memo(DataTableHeaderInner) as typeof DataTableHeaderInner
